import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { fmtMoney } from "@/lib/format";
import { UploadSimple, MagnifyingGlass, ArrowClockwise, CheckSquare, Warning } from "@phosphor-icons/react";

/**
 * Spare Parts / Required Order — the weekly ordering exercise.
 * Two imports drive the report:
 *   - P201 (current stock): also seeds/refreshes the master.
 *   - P501 (pending orders): sums Pending Qty per part.
 * Operator sets min/max per part; report surfaces `need_qty` when
 * `(current + pending) < min`.
 */

const fmtDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

export default function SparePartsPage() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [onlyNeeded, setOnlyNeeded] = useState(false);
  const [uploading, setUploading] = useState({ stock: false, pending: false });
  const [dirty, setDirty] = useState({}); // part_code -> { min_stock, max_stock }
  const stockRef = useRef(null);
  const pendingRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (onlyNeeded) params.only_needed = true;
      const [rowsRes, sumRes] = await Promise.all([
        api.get("/spare-parts", { params }),
        api.get("/spare-parts/summary"),
      ]);
      setRows(rowsRes.data);
      setSummary(sumRes.data);
      setDirty({});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyNeeded]);

  const onUpload = async (kind, file) => {
    if (!file) return;
    const endpoint = kind === "stock" ? "/spare-parts/import-stock" : "/spare-parts/import-pending";
    const label = kind === "stock" ? "P201 (current stock)" : "P501 (pending orders)";
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(
        `${label} imported · ${data.parts_upserted ?? data.parts_updated ?? 0} parts` +
        (data.parts_zeroed ? ` · ${data.parts_zeroed} zeroed` : "")
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
      if (stockRef.current) stockRef.current.value = "";
      if (pendingRef.current) pendingRef.current.value = "";
    }
  };

  const setLocal = (part_code, field, value) => {
    setDirty((d) => ({
      ...d,
      [part_code]: { ...(d[part_code] || {}), [field]: value },
    }));
  };

  const displayRows = useMemo(
    () =>
      rows.map((r) => {
        const d = dirty[r.part_code] || {};
        return {
          ...r,
          min_stock: d.min_stock !== undefined ? d.min_stock : r.min_stock,
          max_stock: d.max_stock !== undefined ? d.max_stock : r.max_stock,
          _dirty: !!dirty[r.part_code],
        };
      }),
    [rows, dirty]
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return displayRows;
    const s = search.trim().toLowerCase();
    return displayRows.filter(
      (r) =>
        (r.part_code || "").toLowerCase().includes(s) ||
        (r.description || "").toLowerCase().includes(s)
    );
  }, [displayRows, search]);

  const saveDirty = async () => {
    const changes = Object.entries(dirty).map(([part_code, v]) => ({
      part_code,
      min_stock: Number(v.min_stock ?? 0) || 0,
      max_stock: Number(v.max_stock ?? 0) || 0,
    }));
    if (!changes.length) return;
    try {
      const { data } = await api.post("/spare-parts/bulk-min-max", { rows: changes });
      toast.success(
        `Saved ${data.written} target${data.written === 1 ? "" : "s"}` +
        (data.missing?.length ? ` · ${data.missing.length} unknown parts skipped` : "")
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div data-testid="spare-parts-page">
      <PageHeader
        eyebrow="14 / Spares"
        title="Stock Planning"
        subtitle="Weekly exercise — import P201 (current stock) + P501 (pending POs), set min/max per part, and get the order list."
      />
      <div className="px-8 py-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="spares-kpis">
          <Kpi label="Total Parts" value={summary?.total_parts ?? "—"} />
          <Kpi label="Parts w/ Target" value={summary?.parts_with_targets ?? "—"} />
          <Kpi label="Parts To Order" value={summary?.parts_to_order ?? "—"} accent={(summary?.parts_to_order || 0) > 0 ? "text-destructive" : ""} />
          <Kpi label="Order Qty" value={summary?.to_order_qty ?? "—"} />
          <Kpi label="Order ₹" value={fmtMoney(summary?.to_order_value || 0)} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <UploadCard
            title="Current Stock (P201)"
            hint="Amaravati SPARE warehouse. XLSX or CSV. Overwrites all previous current-stock numbers."
            uploading={uploading.stock}
            lastAt={summary?.last_stock_upload}
            fileRef={stockRef}
            onFile={(f) => onUpload("stock", f)}
            testId="upload-stock"
          />
          <UploadCard
            title="Pending Orders (P501)"
            hint="OEM pending POs. XLSX or CSV. Sums `Pending Qty` per Part No."
            uploading={uploading.pending}
            lastAt={summary?.last_pending_upload}
            fileRef={pendingRef}
            onFile={(f) => onUpload("pending", f)}
            testId="upload-pending"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center border border-canvas-line bg-canvas">
            <MagnifyingGlass size={12} weight="bold" className="mx-2 text-ink-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search part code / description"
              data-testid="spares-search"
              className="px-1 py-2 bg-canvas text-sm focus:outline-none w-72"
            />
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={onlyNeeded}
              onChange={(e) => setOnlyNeeded(e.target.checked)}
              data-testid="only-needed"
              className="accent-ink"
            />
            <span className="font-mono text-xs uppercase tracking-wider text-ink-muted">
              Show only parts needing order
            </span>
          </label>
          <button
            type="button"
            onClick={load}
            data-testid="refresh"
            className="ml-auto inline-flex items-center gap-2 px-3 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink"
          >
            <ArrowClockwise size={12} weight="bold" /> Refresh
          </button>
          {dirtyCount > 0 && (
            <button
              type="button"
              onClick={saveDirty}
              data-testid="save-dirty"
              className="inline-flex items-center gap-2 px-3 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-ink/90"
            >
              <CheckSquare size={12} weight="bold" /> Save {dirtyCount} target{dirtyCount === 1 ? "" : "s"}
            </button>
          )}
        </div>

        <div className="border border-canvas-line bg-canvas-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="spares-table">
            <thead className="bg-canvas-panel sticky top-0">
              <tr className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted">
                <th className="px-3 py-2.5">Part Code</th>
                <th className="px-3 py-2.5">Description</th>
                <th className="px-3 py-2.5">HSN</th>
                <th className="px-3 py-2.5 text-right">Price ₹</th>
                <th className="px-3 py-2.5 text-right">Current</th>
                <th className="px-3 py-2.5 text-right">Pending</th>
                <th className="px-3 py-2.5 text-right" title="Sum of qty across all open (non-delivered) customer advance parts lines">Cust&nbsp;Adv</th>
                <th className="px-3 py-2.5 text-right w-20">Min</th>
                <th className="px-3 py-2.5 text-right w-20">Max</th>
                <th className="px-3 py-2.5 text-right">Need</th>
                <th className="px-3 py-2.5 text-right">Order ₹</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-ink-subtle font-mono text-xs">Loading…</td></tr>
              )}
              {!loading && filteredRows.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-ink-subtle font-mono text-xs">
                  No parts. Import a P201 to seed the master.
                </td></tr>
              )}
              {!loading && filteredRows.slice(0, 500).map((r) => {
                const need = Number(r.need_qty) || 0;
                const targetSet = (Number(r.min_stock) || 0) > 0 || (Number(r.max_stock) || 0) > 0;
                return (
                  <tr key={r.part_code}
                      data-testid={`spare-row-${r.part_code}`}
                      className={`border-t border-canvas-line ${need > 0 ? "bg-destructive/5" : ""} ${r._dirty ? "bg-blue-500/10" : ""} hover:bg-canvas-panel/40`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.part_code}</td>
                    <td className="px-3 py-2 text-xs">{r.description}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-ink-muted">{r.hsn_code || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{fmtMoney(r.unit_price)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{Number(r.current_stock) || 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">
                      {Number(r.pending_stock) || 0}
                      {(r.pending_po_count || 0) > 0 && (
                        <div className="text-[9px] font-mono text-ink-subtle">{r.pending_po_count} PO{r.pending_po_count > 1 ? "s" : ""}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">
                      {(Number(r.customer_advance_qty) || 0) > 0
                        ? <span className="font-bold text-[#a35700]" title="Customer-earmarked (open advances)">{Number(r.customer_advance_qty)}</span>
                        : <span className="text-ink-subtle">—</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input
                        type="number"
                        value={r.min_stock || ""}
                        onChange={(e) => setLocal(r.part_code, "min_stock", e.target.value)}
                        data-testid={`min-${r.part_code}`}
                        className="w-16 px-1 py-0.5 bg-canvas border border-canvas-line text-right text-xs tabular-nums focus:outline-none focus:border-ink"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input
                        type="number"
                        value={r.max_stock || ""}
                        onChange={(e) => setLocal(r.part_code, "max_stock", e.target.value)}
                        data-testid={`max-${r.part_code}`}
                        className="w-16 px-1 py-0.5 bg-canvas border border-canvas-line text-right text-xs tabular-nums focus:outline-none focus:border-ink"
                      />
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${need > 0 ? "text-destructive" : ""}`}>
                      {need > 0 ? need : (targetSet ? "0" : <span className="text-ink-subtle font-normal">—</span>)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">
                      {need > 0 ? fmtMoney(r.order_value) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredRows.length > 500 && (
          <div className="flex items-center gap-2 text-xs font-mono text-ink-muted">
            <Warning size={12} weight="bold" /> Showing first 500 of {filteredRows.length}. Search or filter to narrow.
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, accent = "" }) {
  return (
    <div className="border border-canvas-line bg-canvas-card p-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{label}</div>
      <div className={`font-display text-lg font-bold tabular-nums mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

function UploadCard({ title, hint, uploading, lastAt, fileRef, onFile, testId }) {
  return (
    <div className="border border-canvas-line bg-canvas-card p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-bold text-sm">{title}</div>
          <div className="text-[11px] text-ink-muted mt-0.5">{hint}</div>
        </div>
        <label
          className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-wider cursor-pointer ${
            uploading ? "opacity-60 pointer-events-none" : ""
          } bg-ink text-white hover:bg-ink/90`}
          data-testid={testId}
        >
          <UploadSimple size={12} weight="bold" />
          {uploading ? "Uploading…" : "Upload"}
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
        </label>
      </div>
      <div className="font-mono text-[10px] text-ink-subtle">
        Last upload: <span className="text-ink-muted">{fmtDT(lastAt)}</span>
      </div>
    </div>
  );
}
